/**
 * CloudClient — manages cloud connectivity lifecycle.
 *
 * Handles auth, mode transitions, repo resolution, token refresh,
 * and queue flush orchestration. Never throws — all errors result
 * in graceful local-mode fallback.
 *
 * Constitution Principle VI: all timers/intervals tracked and cleaned up in dispose().
 */

import { readFileSync, writeFileSync, mkdirSync, renameSync, existsSync } from 'node:fs';
import { join, dirname, basename } from 'node:path';
import { createHash } from 'node:crypto';
import type { CloudConfig, AuthState, AuthMode, GitMetadata } from '@testrelic/core';
import {
  enforceHttps,
  healthCheck,
  exchangeToken,
  refreshAccessToken,
  isAuthError,
  resolveRepo,
} from './cloud-auth.js';
import type { TokenExchangeResult } from './cloud-auth.js';
import { collectGitMetadata, normalizeGitRemoteUrl, deriveRepoDisplayName, deriveNonGitProjectId, readPackageJsonName } from './git-metadata.js';
import { cleanupExpiredQueue, flushQueue } from './cloud-queue.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOKEN_REFRESH_BUFFER_MS = 300_000; // 5 minutes
const PROJECT_CACHE_TTL_MS = 86_400_000; // 24 hours
const HEALTH_CHECK_INTERVAL_MS = 60_000; // 60 seconds
const FLUSH_TIMEOUT_MS = 30_000; // 30 seconds
const DASHBOARD_SETTINGS_URL = 'https://platform.testrelic.ai/settings/api-keys';

// ---------------------------------------------------------------------------
// Repo Cache
// ---------------------------------------------------------------------------

interface RepoCache {
  readonly repoId: string;
  readonly gitId: string;
  readonly displayName: string;
  readonly resolvedAt: number;
  readonly apiKeyHash: string;
}

// ---------------------------------------------------------------------------
// CloudClient
// ---------------------------------------------------------------------------

export class CloudClient {
  private config: CloudConfig | null;
  private authState: AuthState;
  private gitMetadata: GitMetadata | null = null;
  private repoId: string | null = null;
  private failureReason: string | null = null;
  private flushPromise: Promise<void> | null = null;
  private healthCheckTimer: ReturnType<typeof setInterval> | null = null;

  constructor(cloudConfig: CloudConfig | null) {
    this.config = cloudConfig;
    this.authState = {
      mode: 'pending',
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      orgId: null,
      orgName: null,
      userId: null,
      userName: null,
    };
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  async initialize(): Promise<void> {
    // No config or no API key → local mode immediately
    if (!this.config || !this.config.apiKey) {
      this.setLocalMode('no_api_key');
      process.stderr.write('ℹ TestRelic: No API key configured. Running in local mode.\n');
      return;
    }

    try {
      // HTTPS enforcement
      enforceHttps(this.config.endpoint);

      // Collect git metadata
      this.gitMetadata = collectGitMetadata();

      // Health check
      const healthy = await healthCheck(this.config.endpoint);
      if (!healthy) {
        this.setLocalMode('cloud_unreachable');
        process.stderr.write('⚠ TestRelic: Cloud unreachable. Switching to local mode. Data will be queued for later upload.\n');
        return;
      }

      // Token exchange
      const result = await exchangeToken(this.config.endpoint, this.config.apiKey, this.config.timeout);
      if (isAuthError(result)) {
        this.handleAuthError(result.code, result.statusCode);
        return;
      }

      // Success — cloud mode
      const tokenResult = result as TokenExchangeResult;
      this.authState = {
        mode: 'cloud',
        accessToken: tokenResult.accessToken,
        refreshToken: tokenResult.refreshToken,
        expiresAt: Date.now() + tokenResult.expiresIn * 1000,
        orgId: tokenResult.orgId,
        orgName: tokenResult.orgName,
        userId: tokenResult.userId,
        userName: tokenResult.userName,
      };

      process.stderr.write(`✓ TestRelic: Connected to cloud (${tokenResult.orgName} / ${tokenResult.userName})\n`);

      // Repo resolution
      await this.resolveRepoId();

      // Queue cleanup and background flush
      if (this.config.queueDirectory) {
        cleanupExpiredQueue(this.config.queueDirectory, this.config.queueMaxAge);
        this.startBackgroundFlush();
      }

      // Start periodic health check for network recovery
      this.startHealthCheck();
    } catch (err) {
      this.setLocalMode('unexpected_error');
      process.stderr.write(`⚠ TestRelic: Unexpected error during cloud initialization. Running in local mode. ${err instanceof Error ? err.message : String(err)}\n`);
    }
  }

  getMode(): AuthMode {
    return this.authState.mode;
  }

  isCloudMode(): boolean {
    return this.authState.mode === 'cloud';
  }

  isLocalMode(): boolean {
    return this.authState.mode === 'local';
  }

  getAccessToken(): string | null {
    return this.authState.accessToken;
  }

  getRepoId(): string | null {
    return this.repoId;
  }

  getGitMetadata(): GitMetadata | null {
    return this.gitMetadata;
  }

  getConfig(): CloudConfig | null {
    return this.config;
  }

  getFailureReason(): string | null {
    return this.failureReason;
  }

  getEndpoint(): string {
    return this.config?.endpoint ?? 'https://platform.testrelic.ai/api/v1';
  }

  /**
   * Ensure the access token is valid. Refreshes if expiring within 5 minutes.
   * On refresh failure, switches to local mode.
   */
  async ensureValidToken(): Promise<boolean> {
    if (!this.isCloudMode() || !this.authState.accessToken) return false;

    const expiresAt = this.authState.expiresAt ?? 0;
    if (Date.now() + TOKEN_REFRESH_BUFFER_MS < expiresAt) {
      return true; // Token still valid
    }

    // Attempt refresh
    if (!this.config || !this.authState.refreshToken) {
      this.switchToLocalMode('token_expired_no_refresh');
      return false;
    }

    try {
      const refreshResult = await refreshAccessToken(
        this.config.endpoint,
        this.authState.refreshToken,
        this.config.timeout,
      );

      if (!refreshResult) {
        this.switchToLocalMode('token_refresh_failed');
        return false;
      }

      this.authState.accessToken = refreshResult.accessToken;
      this.authState.refreshToken = refreshResult.refreshToken;
      this.authState.expiresAt = Date.now() + refreshResult.expiresIn * 1000;
      return true;
    } catch {
      this.switchToLocalMode('token_refresh_error');
      return false;
    }
  }

  /**
   * Switch from cloud to local mode. One-way transition during a run.
   */
  switchToLocalMode(reason: string): void {
    if (this.authState.mode === 'local') return;
    this.authState.mode = 'local';
    this.failureReason = reason;
    process.stderr.write(`⚠ TestRelic: Switched to local mode (${reason}).\n`);
  }

  /**
   * Clean up all resources: timers, pending requests, state.
   */
  async dispose(): Promise<void> {
    // Stop health check
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }

    // Wait for background flush (with timeout)
    if (this.flushPromise) {
      try {
        await Promise.race([
          this.flushPromise,
          new Promise<void>((resolve) => setTimeout(resolve, FLUSH_TIMEOUT_MS)),
        ]);
      } catch {
        // Ignore flush errors during dispose
      }
      this.flushPromise = null;
    }

    // Clear auth state
    this.authState = {
      mode: 'local',
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      orgId: null,
      orgName: null,
      userId: null,
      userName: null,
    };
    this.repoId = null;
    this.gitMetadata = null;
  }

  // -------------------------------------------------------------------------
  // Private Methods
  // -------------------------------------------------------------------------

  private setLocalMode(reason: string): void {
    this.authState.mode = 'local';
    this.failureReason = reason;
  }

  private handleAuthError(code: string, statusCode: number | null): void {
    switch (code) {
      case 'invalid_key':
        this.setLocalMode('invalid_api_key');
        process.stderr.write(
          `⚠ TestRelic: API key is invalid or revoked. Running in local mode.\n` +
          `  → Manage keys: ${DASHBOARD_SETTINGS_URL}\n`,
        );
        break;
      case 'expired_key':
        this.setLocalMode('expired_api_key');
        process.stderr.write(
          `⚠ TestRelic: API key has expired. Running in local mode.\n` +
          `  → Generate a new key: ${DASHBOARD_SETTINGS_URL}\n`,
        );
        break;
      case 'validation_error':
        this.setLocalMode('validation_error');
        process.stderr.write(
          `⚠ TestRelic: API key format invalid. Running in local mode.\n` +
          `  → Manage keys: ${DASHBOARD_SETTINGS_URL}\n`,
        );
        break;
      case 'rate_limited':
        this.setLocalMode('rate_limited');
        process.stderr.write('⚠ TestRelic: Rate limited during authentication. Running in local mode.\n');
        break;
      case 'timeout':
        this.setLocalMode('auth_timeout');
        process.stderr.write('⚠ TestRelic: Authentication timed out. Running in local mode.\n');
        break;
      default:
        this.setLocalMode(`auth_error_${statusCode ?? 'unknown'}`);
        process.stderr.write('⚠ TestRelic: Cloud authentication failed. Running in local mode.\n');
        break;
    }
  }

  private async resolveRepoId(): Promise<void> {
    if (!this.config || !this.authState.accessToken) return;

    const gitId = this.gitMetadata?.remoteUrl
      ? normalizeGitRemoteUrl(this.gitMetadata.remoteUrl)
      : null;

    const displayName = gitId
      ? deriveRepoDisplayName(gitId)
      : this.config.projectName ?? deriveNonGitProjectId(process.cwd());

    const effectiveGitId = gitId ?? (this.config.projectName ?? deriveNonGitProjectId(process.cwd()));

    // Check cache first
    const cached = this.readRepoCache(effectiveGitId);
    if (cached) {
      this.repoId = cached.repoId;
      return;
    }

    // Resolve from cloud
    try {
      const resolved = await resolveRepo(
        this.config.endpoint,
        this.authState.accessToken,
        effectiveGitId,
        displayName,
        this.config.timeout,
        this.gitMetadata?.branch,
      );

      if (resolved) {
        this.repoId = resolved.repoId;
        this.writeRepoCache(effectiveGitId, resolved.repoId, resolved.displayName);
      }
    } catch {
      // Non-fatal — repo resolution failure doesn't block uploads
    }

    if (!gitId && !this.config.projectName && !readPackageJsonName(process.cwd())) {
      process.stderr.write(
        'ℹ TestRelic: No git remote or package.json detected. Set project.name in .testrelic for stable project identity.\n',
      );
    }
  }

  private readRepoCache(gitId: string): RepoCache | null {
    if (!this.config) return null;
    const cachePath = join(this.config.queueDirectory, '..', 'cache', 'repo.json');
    try {
      if (!existsSync(cachePath)) return null;
      const raw = readFileSync(cachePath, 'utf-8');
      const cache = JSON.parse(raw) as RepoCache;
      if (cache.gitId !== gitId) return null;
      if (Date.now() - cache.resolvedAt > PROJECT_CACHE_TTL_MS) return null;
      // Check API key hash matches
      const currentKeyHash = this.hashApiKey();
      if (currentKeyHash && cache.apiKeyHash !== currentKeyHash) return null;
      return cache;
    } catch {
      return null;
    }
  }

  private writeRepoCache(gitId: string, repoId: string, displayName: string): void {
    if (!this.config) return;
    const cacheDir = join(this.config.queueDirectory, '..', 'cache');
    const cachePath = join(cacheDir, 'repo.json');
    try {
      mkdirSync(cacheDir, { recursive: true });
      const cache: RepoCache = {
        repoId,
        gitId,
        displayName,
        resolvedAt: Date.now(),
        apiKeyHash: this.hashApiKey() ?? '',
      };
      const tmpPath = cachePath + '.tmp';
      writeFileSync(tmpPath, JSON.stringify(cache, null, 2), 'utf-8');
      renameSync(tmpPath, cachePath);
    } catch {
      // Non-fatal
    }
  }

  private hashApiKey(): string | null {
    if (!this.config?.apiKey) return null;
    return createHash('sha256').update(this.config.apiKey).digest('hex').substring(0, 16);
  }

  private startBackgroundFlush(): void {
    if (!this.config || !this.authState.accessToken) return;
    const accessToken = this.authState.accessToken;
    const queueDir = this.config.queueDirectory;
    const endpoint = this.config.endpoint;

    this.flushPromise = Promise.race([
      flushQueue(queueDir, endpoint, accessToken),
      new Promise<void>((resolve) => setTimeout(resolve, FLUSH_TIMEOUT_MS)),
    ]).catch(() => {
      // Background flush errors are non-fatal
    });
  }

  private startHealthCheck(): void {
    if (!this.config) return;
    const endpoint = this.config.endpoint;

    this.healthCheckTimer = setInterval(async () => {
      // Only useful if we've switched to local mode due to connectivity
      if (this.isCloudMode()) return;
      if (!this.failureReason || !['cloud_unreachable', 'auth_timeout', 'network_error'].includes(this.failureReason)) return;

      try {
        const healthy = await healthCheck(endpoint);
        if (!healthy) return;

        // Attempt re-auth
        if (this.config?.apiKey) {
          const result = await exchangeToken(this.config.endpoint, this.config.apiKey, this.config.timeout);
          if (!isAuthError(result)) {
            const tokenResult = result as TokenExchangeResult;
            this.authState = {
              mode: 'cloud',
              accessToken: tokenResult.accessToken,
              refreshToken: tokenResult.refreshToken,
              expiresAt: Date.now() + tokenResult.expiresIn * 1000,
              orgId: tokenResult.orgId,
              orgName: tokenResult.orgName,
              userId: tokenResult.userId,
              userName: tokenResult.userName,
            };
            this.failureReason = null;
            process.stderr.write('✓ TestRelic: Cloud connectivity restored.\n');
            this.startBackgroundFlush();
          }
        }
      } catch {
        // Health check errors are silently ignored
      }
    }, HEALTH_CHECK_INTERVAL_MS);
  }
}
